import DashboardCard, { EmptyState } from './DashboardCard';
import type { DashboardBirthday } from '../../types/dashboard';

import { useDashboardNav } from './useDashboardNav';

interface Props {
  data: DashboardBirthday[];
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

// The card's styles live in Dashboard.css; the icon column and the coloured
// day badge (restored 2026-10-22 after the plain pass) are inline so this card
// stays self-contained.
const ICON_COL_STYLE: React.CSSProperties = {
  flex: '0 0 28px',
  fontSize: '1.1rem',
  color: 'var(--text-secondary)',
};

function badgeStyle(days: number): React.CSSProperties {
  const [background, color] =
    days === 0 ? ['var(--color-positive-bg)', 'var(--color-positive)']
      : days <= 1 ? ['var(--color-warning-bg)', 'var(--color-warning)']
        : ['var(--primary-blue-tint)', 'var(--primary-blue)'];
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background,
    color,
  };
}

function getDaysLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

export default function UpcomingBirthdaysCard({ data, ...dragProps }: Props) {
  const { openResident } = useDashboardNav();
  return (
    <DashboardCard severity="success" icon="bi-gift" title="Upcoming Birthdays" count={data.length} {...dragProps}>
      {data.length === 0 ? (
        <EmptyState message="No upcoming birthdays this week" />
      ) : (
        <div className="birthday-list">
          {data.map((b) => (
            <div
              key={b.pKey_Patient}
              className={`birthday-item ${b.daysUntil === 0 ? 'birthday-today' : ''}`}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer', background: b.daysUntil === 0 ? 'var(--color-positive-bg)' : undefined }}
              onClick={() => openResident(b.pKey_Patient)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openResident(b.pKey_Patient);
                }
              }}
            >
              <div style={ICON_COL_STYLE} aria-hidden="true">
                {b.daysUntil === 0 ? (
                  <i className="bi bi-balloon-heart-fill" style={{ color: 'var(--color-negative)' }} />
                ) : (
                  <i className="bi bi-gift" />
                )}
              </div>
              <div className="birthday-info">
                <span className="birthday-name">{b.fullName}</span>
                <span className="birthday-detail">
                  Turning {b.age + 1}
                  {b.unit ? ` - ${b.unit}` : ''}
                  {b.room_No ? `, Rm ${b.room_No}` : ''}
                </span>
              </div>
              <div className="birthday-days">
                <span
                  className={`birthday-days-badge ${b.daysUntil === 0 ? 'today' : b.daysUntil <= 1 ? 'soon' : ''}`}
                  style={badgeStyle(b.daysUntil)}
                >
                  {getDaysLabel(b.daysUntil)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  );
}
